<template>
    <form class="module-time-tracker" ref="form">
        <template v-if="loading">
            <loading-indicator/>
        </template>
        <template v-else>
            <form-row label="Mitarbeiter" error-name="employee_id" required>
                <form-select v-model="employee" :options="employees"/>
            </form-row>
            <form-row label="Typ" error-name="type" required>
                <form-select v-model="type" :options="typeOptions" name="type"/>
            </form-row>
            <form-row label="Startwert" error-name="overtime_hours" required>
                <form-input v-model="account" type="text" name="overtime_hours" placeholder="Stunden oder S:MM oder SS:MM"/>
            </form-row>
        </template>
    </form>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import type {TinyEmitter} from 'tiny-emitter';
    import FormInput from '../../../components/form/FormInput.vue';
    import FormRow from '../../../components/form/FormRow.vue';
    import FormSelect from '../../../components/form/FormSelect.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import {formSubmit, getFormData, parseFormData} from '@/library/form';
    import {popupDefaultCancelButton} from '@/library/popup';
    import TopMessageHandler from '../../../library/topMessageHandler';
    import {errorHandler, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import {computed, ref, watch, inject} from 'vue';
    import {useStore} from 'vuex';

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const portuna = usePortuna();

    const props = withDefaults(
        defineProps<{
            id?: number;
        }>(),
        {
            id: 0,
        },
    );

    const store = useStore();

    const typeOptions = computed(() =>
    {
        return [
            {
                value: 0,
                label: 'Überstunden',
            },
            {
                value: 1,
                label: 'Minusstunden',
            },
        ];
    });

    const form = ref<HTMLFormElement>();

    const employee  = ref<number>(0);
    const type      = ref<Nullable<number>>(null);
    const account   = ref<string>('');
    const employees = ref<Record<number, string>>({});
    const loading   = ref<boolean>(true);

    const fetch = () =>
    {
        loading.value = true;
        axios.get(`/employee/time-tracker/overtime/change-start/employee/${employee.value}`)
        .then((response) =>
        {
            if (response.data.status !== XHR_STATUS_DEFAULT)
            {
                throw response;
            }

            const {data} = response.data;
            store.dispatch('form/setFilter', data.filter);
            employees.value = data.employees;

            if (null !== data.overtime_minutes)
            {
                type.value = data.overtime_minutes < 0 ? 1 : 0;
            }

            account.value = data.overtime_account;
            loading.value = false;
            popupEmitter.emit('lazy-load-focus');
        })
        .catch((error) =>
        {
            errorHandler(error);
            popupEmitter.emit('close');
        });
    };

    const post = () =>
    {
        let formData = getFormData(form.value!);
        formSubmit(
            `/employee/time-tracker/overtime/change-start/employee/${employee.value}`,
            parseFormData(formData),
        ).then(() =>
        {
            TopMessageHandler.success('Der Startwert wurde erfolgreich gespeichert.');
            popupEmitter.emit('close');
        });
    };

    const watchEmployee = () =>
    {
        fetch();
    };

    employee.value = props.id;
    popupEmitter.emit('setButtons', [
        popupDefaultCancelButton,
        {
            type: 'success',
            title: 'Speichern',
            icon: 'save',
            cmd: () => post(),
        },
    ] as PopupButton[]);

    fetch();
    watch(employee, watchEmployee);

    defineExpose(Object.assign({}, portuna));
</script>